import { useCallback, useEffect, useRef, useState } from 'react';
import type { Problem, AlgorithmProblem, ClassDesignProblem, FullstackProblem, AgentProblem } from '../data/problems';
import type { TestResult, PyOutput, HttpStepResult, ToolCallResult } from '../utils/codeRunner';

// TestResult / AlgoTestResult / PyOutput are defined in codeRunner (so the JS
// worker can share them without a circular import). Re-exported here for the
// callers that historically imported them from this module.
export type { TestResult, AlgoTestResult, PyOutput } from '../utils/codeRunner';

export interface RunArgs {
  files: Array<{ name: string; code: string }>;
  problem: Problem;
}

// ── Hook ─────────────────────────────────────────────────────────────────────
//
// Owns the Pyodide Web Worker for a `CodeCompiler` instance:
//   • boots one worker on mount, terminates on unmount
//   • routes incoming messages to the most recent run's handler (the `id`
//     match guards against stale workers from a prior run delivering late)
//   • enforces a 7s main-thread deadline on top of the worker's own 5s
//     timeout, so a hung worker never leaves the UI stuck in "running"
//
// The single externally-exposed mutating call is `runPython(args)`. Everything
// else is read state.
//
// NOTE: the worker is per-component instance. A future improvement is hoisting
// it to a module singleton so Pyodide doesn't reboot when navigating between
// problems — see audit item #15.

export interface PyodideController {
  loading: boolean;
  output: PyOutput | null;
  running: boolean;
  results: TestResult[];
  runPython: (args: RunArgs) => void;
  reset: () => void;
  /**
   * Imperatively set test results. Used by the JS / TS runner so it can
   * share the same results array as the Python runner without duplicating
   * the rendering machinery.
   */
  setResults: (r: TestResult[]) => void;
  /** Imperatively set the running flag for JS/TS execution. */
  setRunning: (b: boolean) => void;
  /** Imperatively clear the Python-output panel. */
  setOutput: (o: PyOutput | null) => void;
}

export function usePyodideWorker(): PyodideController {
  const [loading, setLoading] = useState(true);
  const [output, setOutput] = useState<PyOutput | null>(null);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<TestResult[]>([]);

  const workerRef = useRef<Worker | null>(null);
  const handlerRef = useRef<((data: unknown) => void) | null>(null);

  // Boot once on mount, tear down on unmount.
  useEffect(() => {
    const worker = new Worker('/pyodide.worker.js');
    worker.onmessage = ({ data }) => {
      if (data?.type === 'ready') {
        setLoading(false);
        return;
      }
      handlerRef.current?.(data);
    };
    workerRef.current = worker;
    return () => {
      worker.terminate();
      workerRef.current = null;
      handlerRef.current = null;
    };
  }, []);

  const reset = useCallback(() => {
    setResults([]);
    setOutput(null);
  }, []);

  const runPython = useCallback(({ files, problem }: RunArgs) => {
    const worker = workerRef.current;
    if (!worker || loading) return;
    setRunning(true);
    setResults([]);
    setOutput(null);

    const id = crypto.randomUUID();
    const timeoutId = window.setTimeout(() => {
      handlerRef.current = null;
      setRunning(false);
      setOutput({ stdout: '', stderr: '', error: 'Execution timed out (5s limit).' });
    }, 7000);

    handlerRef.current = (data: unknown) => {
      const d = data as Record<string, unknown>;
      if (d['id'] !== id) return;
      window.clearTimeout(timeoutId);
      handlerRef.current = null;
      setRunning(false);

      if (d['type'] === 'timeout') {
        setOutput({ stdout: '', stderr: '', error: 'Execution timed out (5s limit).' });
        return;
      }

      if (d['stdout'] || d['stderr'] || d['error']) {
        setOutput({
          stdout: (d['stdout'] as string) || '',
          stderr: (d['stderr'] as string) || '',
          error: d['error'] as string | undefined,
        });
      }

      if (d['type'] === 'test_results' && d['testResults']) {
        const raw = d['testResults'] as Array<Record<string, unknown>>;
        if (problem.type === 'algorithm') {
          const algoProb = problem as AlgorithmProblem;
          setResults(raw.map((r, i): TestResult => ({
            kind: 'algo',
            input: algoProb.testCases[i]?.input ?? {},
            expected: algoProb.testCases[i]?.expected,
            validateName: algoProb.testCases[i]?.validate,
            actual: r['actual'],
            passed: r['passed'] as boolean,
            error: r['error'] as string | undefined,
            stdout: (r['stdout'] as string | undefined) ?? '',
            stderr: (r['stderr'] as string | undefined) ?? '',
          })));
        } else if (problem.type === 'fullstack') {
          const fsProb = problem as FullstackProblem;
          setResults(raw.map((r, i): TestResult => ({
            kind: 'fullstack',
            label: fsProb.testCases[i]?.label ?? `Case ${i + 1}`,
            passed: r['passed'] as boolean,
            error: r['error'] as string | undefined,
            stdout: (r['stdout'] as string | undefined) ?? '',
            stderr: (r['stderr'] as string | undefined) ?? '',
            steps: ((r['steps'] ?? []) as Array<Record<string, unknown>>).map((s): HttpStepResult => ({
              label: s['label'] as string,
              method: s['method'] as string,
              path: s['path'] as string,
              expectedStatus: s['expectedStatus'] as number | undefined,
              actualStatus: s['actualStatus'] as number | undefined,
              expected: s['expected'],
              actual: s['actual'],
              passed: s['passed'] as boolean,
            })),
          })));
        } else if (problem.type === 'agent') {
          const agentProb = problem as AgentProblem;
          setResults(raw.map((r, i): TestResult => ({
            kind: 'agent',
            label: agentProb.testCases[i]?.label ?? `Case ${i + 1}`,
            passed: r['passed'] as boolean,
            error: r['error'] as string | undefined,
            stdout: (r['stdout'] as string | undefined) ?? '',
            stderr: (r['stderr'] as string | undefined) ?? '',
            finalAnswer: r['finalAnswer'],
            expectedFinal: r['expectedFinal'],
            calls: ((r['calls'] ?? []) as Array<Record<string, unknown>>).map((c): ToolCallResult => ({
              index: c['index'] as number,
              name: c['name'] as string | undefined,
              arguments: c['arguments'],
              expectedName: c['expectedName'] as string | null | undefined,
              passed: c['passed'] as boolean,
            })),
          })));
        } else {
          const classProb = problem as ClassDesignProblem;
          setResults(raw.map((r, i): TestResult => ({
            kind: 'class',
            label: classProb.testCases[i]?.label ?? `Case ${i + 1}`,
            passed: r['passed'] as boolean,
            error: r['error'] as string | undefined,
            stdout: (r['stdout'] as string | undefined) ?? '',
            stderr: (r['stderr'] as string | undefined) ?? '',
            ops: ((r['ops'] ?? []) as Array<Record<string, unknown>>).map(op => ({
              op: op['op'] as string,
              args: op['args'] as unknown[],
              expected: op['expected'],
              actual: op['actual'],
              passed: op['passed'] as boolean,
            })),
          })));
        }
      }
    };

    if (problem.type === 'algorithm') {
      const algoProb = problem as AlgorithmProblem;
      worker.postMessage({
        id, type: 'algo_tests',
        files,
        pyEvalCode: algoProb.pyEvalCode,
        testCases: algoProb.testCases,
        validatorsPy: algoProb.validators?.python ?? '',
      });
    } else if (problem.type === 'fullstack') {
      const fsProb = problem as FullstackProblem;
      const entry = fsProb.files?.python?.find(f => f.isEntry)?.name;
      worker.postMessage({
        id, type: 'fullstack_tests',
        files,
        appExport: fsProb.appExport ?? 'app',
        entryFile: entry,
        testCases: fsProb.testCases,
      });
    } else if (problem.type === 'agent') {
      const agentProb = problem as AgentProblem;
      const entry = agentProb.files?.python?.find(f => f.isEntry)?.name;
      worker.postMessage({
        id, type: 'agent_tests',
        files,
        agentExport: agentProb.agentExport ?? 'run_agent',
        entryFile: entry,
        testCases: agentProb.testCases,
      });
    } else {
      const classProb = problem as ClassDesignProblem;
      worker.postMessage({
        id, type: 'class_tests',
        files,
        className: classProb.className,
        testCases: classProb.testCases,
      });
    }
  }, [loading]);

  return {
    loading,
    output,
    running,
    results,
    runPython,
    reset,
    setResults,
    setRunning,
    setOutput,
  };
}
